/* eslint-disable @typescript-eslint/no-floating-promises */
import { Button } from "@mui/material";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiResponse,
  ReturnNodeDto,
  SeaSurveyDetailsDto,
} from "~/api/GeneratedApi";
import GenericStepperDialog, {
  DialogStep,
} from "~/components/GenericStepperDialog";
import { useFileUpload } from "~/components/useFileUpload";
import LoadingBackdrop from "~/template/components/LoadingBackdrop";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import SeaSurveyForm, { SeaSurveyFormInputs } from "./SeaSurveyForm";

export type SeaSurveyDialogAction = "add" | "edit" | "view";
type Props = {
  activeAction?: SeaSurveyDialogAction;
  seaSurvey?: SeaSurveyDetailsDto;
  onClose: () => void;
  onAdd: (d: SeaSurveyFormInputs) => Promise<ReturnNodeDto> | null;
  onEdit: (d: SeaSurveyFormInputs) => Promise<ApiResponse> | null;
};

const SeaSurveyAddEditDialog = ({
  activeAction,
  seaSurvey,
  onClose,
  onAdd,
  onEdit,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("sea-survey");
  const [submitLoading, setSubmitLoading] = React.useState(false);

  const actions: SeaSurveyDialogAction[] = ["add", "edit", "view"];
  const formId = useMemo(
    () => `${activeAction}-sea-survey-form`,
    [activeAction],
  );

  const { uploadFiles } = useFileUpload();

  const { mutateAsync: uploadFilesMutation, isLoading } = useCustomMutation(
    ({ id, files }: { id: number; files: File[] }) => uploadFiles(id, files),
  );

  const edit = useMemo(() => activeAction === "edit", [activeAction]);
  const add = useMemo(() => activeAction === "add", [activeAction]);
  const view = useMemo(() => activeAction === "view", [activeAction]);

  const steps: DialogStep[] = useMemo(() => {
    let title = "";
    switch (activeAction) {
      case "add":
        title = t("fill-info");
        break;
      case "edit":
        title = t("edit-info");
        break;
      case "view":
        title = seaSurvey?.name ?? "";
        break;
      default:
        break;
    }
    return [
      {
        title,
        content: (
          <SeaSurveyForm
            onSubmit={(d, files, dirty) => {
              if (add) {
                setSubmitLoading(true);
                onAdd(d)?.then((node) => {
                  if (files.length === 0) {
                    onClose();
                    return;
                  }
                  uploadFilesMutation(
                    { id: node.id, files },
                    { onSuccess: () => onClose?.() },
                  );
                });
              } else if (edit) {
                const promises: Promise<unknown>[] = [];
                if (dirty) {
                  setSubmitLoading(true);
                  const ed = onEdit(d);
                  if (ed) promises.push(ed);
                }
                if (files.length !== 0) {
                  promises.push(
                    uploadFilesMutation({ id: seaSurvey!.folderId, files }),
                  );
                }
                Promise.all(promises)
                  .then(() => {
                    onClose?.();
                  })
                  .finally(() => setSubmitLoading(false));
              }
            }}
            formId={formId}
            defaultValues={seaSurvey ?? undefined}
            readOnly={view}
          />
        ),
      },
    ];
  }, [
    activeAction,
    formId,
    seaSurvey,
    view,
    t,
    add,
    edit,
    onAdd,
    uploadFilesMutation,
    onEdit,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const renderNextButton = useCallback(
    () =>
      view ? null : (
        <Button
          type="submit"
          form={formId}
          variant="contained"
          color="secondary"
          disabled={submitLoading}
        >
          {t("general:save")}
        </Button>
      ),
    [view, formId, submitLoading, t],
  );

  const renderBackButton = useCallback(
    (_step, _back, handleDialogClose) => {
      return (
        <Button
          onClick={() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            handleDialogClose();
            handleClose();
          }}
        >
          {view ? t("general:close") : t("general:cancel")}
        </Button>
      );
    },
    [handleClose, t, view],
  );

  if (!activeAction || !actions.includes(activeAction)) return null;

  return (
    <>
      <LoadingBackdrop open={isLoading} text={t("uploading-files")} />
      <GenericStepperDialog
        open
        hideSteps
        steps={steps}
        onClose={handleClose}
        dialogTitle=""
        minHeight="40%"
        renderNextButton={renderNextButton}
        renderBackButton={renderBackButton}
      />
    </>
  );
};

export default SeaSurveyAddEditDialog;
